import {
  OptionComponent,
  PageSettings,
  RadioSettings,
  RadioQuestion,
  useSection,
} from "@tree-company/tree-query";
import { PageComponent } from "../components/page";
import { translationFromKey } from "./helpers";
import clsx from "clsx";
import { Tooltip } from "../components/tooltip";

const scales = [
  "price",
  "stable_price",
  "adjusted_formula",
  "origin",
  "source",
  "customer_satisfaction",
  "invests_in_renewable",
  "belgian_ownership",
  "return_on_investment",
  "tackles_energy_poverty",
];

const ScaleQuestionComponent = ({
  question,
  className,
  handleChange,
}: {
  question: RadioQuestion;
  className: string;
  handleChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string | number | boolean,
  ) => void;
}) => {
  const {
    value,
    title,
    description,
    htmlAttributes,
    key,
    required,
    hint,
    errorMessages,
    valid,
    options,
    disabled,
    ariaDisabled,
  } = useSection(question);

  return (
    <fieldset
      className={clsx(
        "question",
        "question--radio",
        htmlAttributes.class,
        { "question--required": required },
        { "question--invalid": !valid },
        { "question--disabled": disabled || ariaDisabled },
        className,
      )}
      name={key}
      disabled={disabled}
      aria-disabled={ariaDisabled}
      data-testid="fieldset"
    >
      <legend className="question__label">
        {title}
        {description && (
          <Tooltip>
            <p className="question__description">{description}</p>
          </Tooltip>
        )}
      </legend>

      <div className="question__options">
        {options.map((o) => (
          <OptionComponent
            baseKey={key}
            option={o}
            className="question__option"
            type="radio"
            key={o.value.toString()}
            checked={value === o.value}
            onChange={(e) => handleChange(e, o.value)}
          />
        ))}
      </div>
      {hint && <span className="question__hint">{hint}</span>}
      {errorMessages.length > 0 && (
        <span className="question__error">{errorMessages[0]}</span>
      )}
    </fieldset>
  );
};

function makeScaleQuestion(
  key: string,
  translation_key: string,
  order = 0,
): RadioSettings {
  return {
    type: "radio",
    key: key,
    order: order,
    title: translationFromKey([translation_key, "text"]),
    description: translationFromKey([translation_key, "description"]),
    htmlAttributes: { class: "question--scale" },
    component: ScaleQuestionComponent,
    options: [
      {
        value: "1",
        label: translationFromKey("layout.scale_options.1"),
      },
      {
        value: "2",
        label: translationFromKey("layout.scale_options.2"),
      },
      {
        value: "3",
        label: translationFromKey("layout.scale_options.3"),
      },
      {
        value: "4",
        label: translationFromKey("layout.scale_options.4"),
      },
      {
        value: "5",
        label: translationFromKey("layout.scale_options.5"),
      },
    ],
    required: true,
  };
}

function makeScalePages(): PageSettings[] {
  return scales.map((key, index) => {
    const question = makeScaleQuestion(key, `questions.${key}`);

    return {
      key: `page_${key}`,
      title: {},
      order: index + 4,
      sections: [question],
      component: PageComponent,
    };
  });
}

export const scale_pages = makeScalePages();
